import { DashboardWidget } from '@adtrackify/at-tracking-event-types';
import { Layout } from 'react-grid-layout';

import { BREAKPOINT_TIER } from '@/features/dashboard/types/grid-enums';
import { COLS_BY_TIER } from '@/features/dashboard/constants/grid-constants';
import { getDimensionsForDensity } from './widget-sizes';
import { WIDGET_CONSTRAINTS } from '../constants/widgets-constants';
import { repackLayoutItemsForBreakpoint } from './build-layout-items';

import { DASHBOARD_DENSITY } from '@/features/dashboard/types/dashboard-density-enums';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

export function getWidgetDimensions(
  widget: DashboardWidget,
  density: DASHBOARD_DENSITY,
  tier: BREAKPOINT_TIER
): { w: number; h: number } {
  const breakpointCols = COLS_BY_TIER[tier];
  const customDims = widget.layout?.customDimensions;
  const densityDims = getDimensionsForDensity(widget.chartType, density, tier);

  const w = customDims?.width ?? densityDims.w;
  const h = customDims?.height ?? densityDims.h;

  return {
    w: Math.max(1, Math.min(w, breakpointCols)),
    h: Math.max(1, h),
  };
}

export function buildLayoutItem(
  widget: DashboardWidget,
  x: number,
  y: number,
  density: DASHBOARD_DENSITY,
  tier: BREAKPOINT_TIER
): Layout {
  const breakpointCols = COLS_BY_TIER[tier];
  const { w, h } = getWidgetDimensions(widget, density, tier);
  const constraints = WIDGET_CONSTRAINTS[widget.chartType ?? 'area'];

  return {
    i: widget.id,
    x: Math.min(x, breakpointCols - w),
    y,
    w,
    h,
    minW: constraints?.minW ?? 2,
    maxW: Math.min(constraints?.maxW ?? breakpointCols, breakpointCols),
    minH: constraints?.minH ?? 1,
    maxH: Math.max(h, constraints?.maxH ?? 6),
  };
}

export function hasStaleWidths(
  widgets: DashboardWidget[],
  density: DASHBOARD_DENSITY,
  tier: BREAKPOINT_TIER
): boolean {
  for (const widget of widgets) {
    const savedCols = widget.layout?.columns;
    if (savedCols === undefined || savedCols === null) continue;

    const { w } = getWidgetDimensions(widget, density, tier);
    if (savedCols !== w) return true;
  }

  return false;
}

function getWidgetLayoutItem(widget: DashboardWidget): Layout {
  const constraints = WIDGET_CONSTRAINTS[widget.chartType ?? 'area'];
  const layout = widget.layout;

  return {
    i: widget.id,
    x: layout?.x ?? 0,
    y: layout?.y ?? 0,
    w: layout?.columns ?? constraints?.w ?? 3,
    h: layout?.rows ?? constraints?.h ?? 1,
    minW: layout?.minColumns ?? constraints?.minW,
    maxW: layout?.maxColumns ?? constraints?.maxW,
    minH: layout?.minRows ?? constraints?.minH,
    maxH: layout?.maxRows ?? constraints?.maxH,
  };
}

function getNormalizedLayoutUpdate(
  widget: DashboardWidget,
  currentItem = getWidgetLayoutItem(widget)
): Partial<Layout> {
  const constraints = WIDGET_CONSTRAINTS[widget.chartType ?? 'area'];
  const layout = widget.layout;
  const minW = constraints?.minW ?? layout?.minColumns ?? 1;
  const maxW = constraints?.maxW ?? layout?.maxColumns ?? COLS_BY_TIER[BREAKPOINT_TIER.DESKTOP];
  const minH = constraints?.minH ?? layout?.minRows ?? 1;
  const maxH = constraints?.maxH ?? layout?.maxRows ?? 6;
  const w = clampLayoutValue(currentItem.w ?? constraints?.w ?? minW, minW, maxW);
  const h = clampLayoutValue(currentItem.h ?? constraints?.h ?? minH, minH, maxH);

  return {
    x: clampLayoutValue(currentItem.x ?? 0, 0, Math.max(0, COLS_BY_TIER[BREAKPOINT_TIER.DESKTOP] - w)),
    y: Math.max(0, currentItem.y ?? 0),
    w,
    h,
    minW,
    maxW,
    minH,
    maxH,
  };
}

function clampLayoutValue(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function isLayoutGeometryChanged(item: Layout, updates: Partial<Layout>): boolean {
  return item.x !== updates.x || item.y !== updates.y || item.w !== updates.w || item.h !== updates.h;
}

function isWidgetLayoutInvalid(widget: DashboardWidget): boolean {
  const item = getWidgetLayoutItem(widget);
  const update = getNormalizedLayoutUpdate(widget);
  return isLayoutGeometryChanged(item, update);
}

function mergeLayoutItemsIntoWidgets(
  widgets: DashboardWidget[],
  layoutItems: Layout[]
): DashboardWidget[] {
  const layoutById = new Map(layoutItems.map(item => [item.i, item]));

  return widgets.map(widget => {
    const item = layoutById.get(widget.id);
    if (!item) return widget;

    return {
      ...widget,
      layout: {
        ...widget.layout,
        x: item.x,
        y: item.y,
        columns: item.w,
        rows: item.h,
        minColumns: item.minW ?? widget.layout.minColumns,
        maxColumns: item.maxW ?? widget.layout.maxColumns,
        minRows: item.minH ?? widget.layout.minRows,
        maxRows: item.maxH ?? widget.layout.maxRows,
      },
    };
  });
}

function getSortedInvalidLayoutItems(layoutItems: Layout[], invalidIds: Set<string>): Layout[] {
  return layoutItems
    .filter(item => invalidIds.has(item.i))
    .sort((a, b) => (a.y === b.y ? a.x - b.x : a.y - b.y));
}

function doesCandidateFit(candidate: Layout, placedItems: Layout[]): boolean {
  return placedItems.every(item => !doLayoutItemsOverlap(candidate, item));
}

function findFirstAvailablePosition(
  item: Layout,
  placedItems: Layout[],
  startY: number
): Pick<Layout, 'x' | 'y'> {
  const cols = COLS_BY_TIER[BREAKPOINT_TIER.DESKTOP];
  const maxX = Math.max(0, cols - item.w);

  for (let y = Math.max(0, startY); y < 10000; y += 1) {
    for (let x = 0; x <= maxX; x += 1) {
      if (doesCandidateFit({ ...item, x, y }, placedItems)) {
        return { x, y };
      }
    }
  }

  const bottomY = placedItems.reduce((max, placedItem) => Math.max(max, placedItem.y + placedItem.h), startY);
  return { x: 0, y: bottomY };
}

function retileInvalidLayoutItems(layoutItems: Layout[], invalidIds: Set<string>): Layout[] {
  if (invalidIds.size === 0) return layoutItems;

  const invalidItems = getSortedInvalidLayoutItems(layoutItems, invalidIds);
  const placedItems: Layout[] = [];
  for (const item of layoutItems) {
    if (invalidIds.has(item.i)) continue;
    placedItems.push({ ...item });
  }
  const startY = invalidItems.reduce(
    (minY, item) => Math.min(minY, item.y),
    invalidItems[0]?.y ?? 0
  );
  const retiledById = new Map<string, Layout>();

  for (const item of invalidItems) {
    const position = findFirstAvailablePosition(item, placedItems, startY);
    const retiledItem = { ...item, ...position };
    retiledById.set(item.i, retiledItem);
    placedItems.push(retiledItem);
  }

  return layoutItems.map(item => retiledById.get(item.i) ?? item);
}

function doLayoutItemsOverlap(a: Layout, b: Layout): boolean {
  return (
    a.x < b.x + b.w &&
    a.x + a.w > b.x &&
    a.y < b.y + b.h &&
    a.y + a.h > b.y
  );
}

function doLayoutItemsOverlapHorizontally(a: Layout, b: Layout): boolean {
  return a.x < b.x + b.w && a.x + a.w > b.x;
}

function pushCollidingItemsDown(layout: Layout[], sourceId: string): void {
  const layoutById = new Map(layout.map(item => [item.i, item]));
  const queue = [sourceId];

  while (queue.length > 0) {
    const currentId = queue.shift();
    const current = currentId !== undefined ? layoutById.get(currentId) : undefined;
    if (!current) continue;

    const collidingItems = layout
      .filter(item => item.i !== current.i && doLayoutItemsOverlap(current, item))
      .sort((a, b) => (a.y === b.y ? a.x - b.x : a.y - b.y));

    for (const collidingItem of collidingItems) {
      const nextY = current.y + current.h;
      if (collidingItem.y >= nextY) continue;

      collidingItem.y = nextY;
      queue.push(collidingItem.i);
    }
  }
}

function pullOverlappingItemsUp(layout: Layout[], source: Layout, oldSourceBottom: number): void {
  const candidates = layout
    .filter(
      item =>
        item.i !== source.i &&
        item.y >= oldSourceBottom &&
        doLayoutItemsOverlapHorizontally(item, source)
    )
    .sort((a, b) => (a.y === b.y ? a.x - b.x : a.y - b.y));

  for (const candidate of candidates) {
    const highestBlockingBottom = layout
      .filter(
        item =>
          item.i !== candidate.i &&
          item.y < candidate.y &&
          doLayoutItemsOverlapHorizontally(item, candidate)
      )
      .reduce((maxBottom, item) => Math.max(maxBottom, item.y + item.h), 0);

    if (highestBlockingBottom < candidate.y) {
      candidate.y = highestBlockingBottom;
    }
  }
}

export function resolveLocalizedLayoutChange(
  layout: Layout[],
  itemId: string,
  updates: Partial<Layout>
): Layout[] {
  const nextLayout = layout.map(item => ({ ...item }));
  const target = nextLayout.find(item => item.i === itemId);
  if (!target) return nextLayout;

  const oldBottom = target.y + target.h;
  const oldHeight = target.h;

  Object.assign(target, updates);

  pushCollidingItemsDown(nextLayout, itemId);

  if (target.h < oldHeight) {
    pullOverlappingItemsUp(nextLayout, target, oldBottom);
  }

  return nextLayout;
}

export function normalizeDashboardWidgetLayouts(widgets: DashboardWidget[]): DashboardWidget[] {
  let nextLayout = widgets.map(getWidgetLayoutItem);
  const invalidIds = new Set<string>();
  for (const widget of widgets) {
    if (isWidgetLayoutInvalid(widget)) {
      invalidIds.add(widget.id);
    }
  }
  const layoutById = new Map(nextLayout.map(item => [item.i, item]));

  for (const widget of widgets) {
    if (!invalidIds.has(widget.id)) continue;

    const currentItem = layoutById.get(widget.id);
    if (!currentItem) continue;

    const normalizedUpdate = getNormalizedLayoutUpdate(widget, currentItem);
    if (!isLayoutGeometryChanged(currentItem, normalizedUpdate)) continue;

    Object.assign(currentItem, normalizedUpdate);
  }

  nextLayout = retileInvalidLayoutItems(nextLayout, invalidIds);

  return mergeLayoutItemsIntoWidgets(widgets, nextLayout);
}

export function hasInvalidDashboardWidgetLayout(widgets: DashboardWidget[]): boolean {
  return widgets.some(isWidgetLayoutInvalid);
}

export function getInvalidDashboardWidgetLayoutIds(widgets: DashboardWidget[]): string[] {
  const ids: string[] = [];
  for (const widget of widgets) {
    if (isWidgetLayoutInvalid(widget)) {
      ids.push(widget.id);
    }
  }
  return ids;
}

// ---------------------------------------------------------------------------
// Layout building
// ---------------------------------------------------------------------------

export function buildLayoutForTier(
  widgets: DashboardWidget[],
  density: DASHBOARD_DENSITY,
  tier: BREAKPOINT_TIER
): Layout[] {
  if (!widgets || widgets.length === 0) return [];

  // Persisted x/y coordinates belong to the editable 12-column desktop grid.
  // Responsive tiers are always derived from canonical widget order. Width
  // alone cannot identify the source grid: compact desktop and mobile both
  // use three-column widgets even though their grids have 12 and 6 columns —
  // `isMobileDerivedLayout` closes that gap with a structural check (saved
  // arrangement differs from the desktop packing while fitting entirely
  // inside the six-column grid). Desktop-authored records
  // (`isRecordDesktopAuthored`) are exempt: their arrangement is ground
  // truth even when it lives entirely in the left six columns.
  if (
    tier === BREAKPOINT_TIER.DESKTOP &&
    canUseSavedPositions(widgets, density, tier) &&
    (!isMobileDerivedLayout(widgets, density) || isRecordDesktopAuthored(widgets))
  ) {
    return buildLayoutItemsFromSavedPositions(widgets, density, tier);
  }

  return repackLayoutItemsForBreakpoint(widgets, density, tier);
}

export function canUseSavedPositions(
  widgets: DashboardWidget[],
  density: DASHBOARD_DENSITY,
  tier: BREAKPOINT_TIER
): boolean {
  if (!widgets || widgets.length === 0) return false;

  for (const widget of widgets) {
    const layout = widget.layout;
    if (!layout) return false;
    if (layout.x === undefined || layout.x === null) return false;
    if (layout.y === undefined || layout.y === null) return false;
    if (layout.columns === undefined || layout.columns === null) return false;
    if (layout.rows === undefined || layout.rows === null) return false;

    // getWidgetDimensions already accounts for customDimensions
    const { w } = getWidgetDimensions(widget, density, tier);
    if (layout.columns !== w) return false;
  }

  return true;
}

interface WidgetPosition {
  x: number;
  y: number;
}

type PositionMap = Map<string, WidgetPosition>;

function savedPositionMap(widgets: DashboardWidget[]): PositionMap {
  const map = new Map<string, WidgetPosition>();
  for (const widget of widgets) {
    map.set(widget.id, { x: widget.layout?.x ?? 0, y: widget.layout?.y ?? 0 });
  }
  return map;
}

function layoutPositionMap(items: Layout[]): PositionMap {
  const map = new Map<string, WidgetPosition>();
  for (const item of items) {
    map.set(item.i, { x: item.x, y: item.y });
  }
  return map;
}

function positionMapsEqual(a: PositionMap, b: PositionMap): boolean {
  if (a.size !== b.size) return false;
  for (const [id, position] of a) {
    const other = b.get(id);
    if (!other || other.x !== position.x || other.y !== position.y) return false;
  }
  return true;
}

export function isMobileDerivedLayout(
  widgets: DashboardWidget[],
  density: DASHBOARD_DENSITY
): boolean {
  if (!widgets || widgets.length === 0) return false;

  for (const widget of widgets) {
    const layout = widget.layout;
    if (!layout) return false;
    if (layout.x === undefined || layout.x === null) return false;
    if (layout.y === undefined || layout.y === null) return false;
  }

  const saved = savedPositionMap(widgets);
  const desktop = layoutPositionMap(
    repackLayoutItemsForBreakpoint(widgets, density, BREAKPOINT_TIER.DESKTOP)
  );
  if (positionMapsEqual(saved, desktop)) return false;

  const mobileCols = COLS_BY_TIER[BREAKPOINT_TIER.MOBILE];
  for (const widget of widgets) {
    const layout = widget.layout;
    const width = layout.columns ?? getWidgetDimensions(widget, density, BREAKPOINT_TIER.DESKTOP).w;
    if ((layout.x ?? 0) + width > mobileCols) return false;
  }

  return true;
}

export function isRecordDesktopAuthored(widgets: DashboardWidget[]): boolean {
  if (!widgets || widgets.length === 0) return false;
  return widgets.every(widget => widget.layout?.isDesktopAuthored === true);
}

function buildLayoutItemsFromSavedPositions(
  widgets: DashboardWidget[],
  density: DASHBOARD_DENSITY,
  tier: BREAKPOINT_TIER
): Layout[] {
  const breakpointCols = COLS_BY_TIER[tier];

  const items: Layout[] = widgets.map(widget => {
    const layout = widget.layout;
    const constraints = WIDGET_CONSTRAINTS[widget.chartType ?? 'area'];
    const { w, h } = getWidgetDimensions(widget, density, tier);

    return {
      i: widget.id,
      x: Math.min(Math.max(0, layout?.x ?? 0), Math.max(0, breakpointCols - w)),
      y: Math.max(0, layout?.y ?? 0),
      w,
      h,
      minW: constraints?.minW ?? 2,
      maxW: Math.min(constraints?.maxW ?? breakpointCols, breakpointCols),
      minH: constraints?.minH ?? 1,
      maxH: Math.max(h, constraints?.maxH ?? 6),
    };
  });

  return items.sort((a, b) => {
    if (a.y !== b.y) return a.y - b.y;
    if (a.x !== b.x) return a.x - b.x;
    return a.i.localeCompare(b.i);
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function buildAllLayouts(
  widgets: DashboardWidget[],
  density: DASHBOARD_DENSITY
): Record<BREAKPOINT_TIER, Layout[]> {
  const layouts = {} as Record<BREAKPOINT_TIER, Layout[]>;
  for (const tier of Object.values(BREAKPOINT_TIER)) {
    layouts[tier] = buildLayoutForTier(widgets, density, tier);
  }
  return layouts;
}
